<template>
  <form @submit.prevent="handleSubmit">
    <b-row>
      <!-- Vehicle -->
      <b-col md="6" class="mb-3">
        <label class="mb-2">
          <strong>
            Fleet Name
            <span class="text-danger">*</span>
          </strong>
        </label>
        <v-select
          v-model="form.vehicle_id"
          :options="vehicleOptions"
          label="name"
          :reduce="(option) => option?.id"
          placeholder="Select Fleet..."
          @input="handleVehicleSelect"
          :disabled="!!selectedData"
        ></v-select>
      </b-col>

      <!-- Vehicle Power ID -->
      <b-col md="6" class="mb-3">
        <label class="mb-2">
          <strong>
            Engine
            <span class="text-danger">*</span>
          </strong>
        </label>
        <v-select
          v-model="form.vehicle_power_id"
          :options="vehiclePowerOptions"
          label="engineName"
          :reduce="(option) => option?.id"
          placeholder="Select Engine..."
        ></v-select>
      </b-col>

      <!-- Effective Date -->
      <b-col md="6" class="mb-3 d-flex flex-column">
        <label class="mb-2">
          <strong>
            Effective Date
            <span class="text-danger">*</span>
          </strong>
        </label>
        <date-picker
          v-model="form.effective_date"
          value-type="YYYY-MM-DD"
          type="date"
          placeholder="Select date"
          class="text-black w-100"
          format="DD-MM-YYYY"
          :input-attr="{ required: true }"
        ></date-picker>
      </b-col>

      <!-- Kick Off GOH (Hours) -->
      <b-col md="6" class="mb-3">
        <label class="mb-2">
          <strong>
            Kick Off GOH (Hours)
            <span class="text-danger">*</span>
          </strong>
        </label>
        <InputCurrency
          prepend=""
          v-model="form.kick_off_goh"
          placeholder="Enter hours"
        ></InputCurrency>
      </b-col>

      <!-- Remarks -->
      <b-col md="12" class="mb-3">
        <label class="mb-2">
          <strong>Remarks</strong>
        </label>
        <b-form-textarea
          v-model="form.remarks"
          rows="4"
          placeholder="Enter remarks..."
        ></b-form-textarea>
      </b-col>
    </b-row>

    <div class="d-flex">
      <b-button
        variant="outline-primary"
        class="w-50 mr-3"
        @click="$emit('close')"
        type="button"
      >
        Cancel
      </b-button>
      <b-button v-if="!loading" variant="primary" type="submit" class="w-50">
        Submit
      </b-button>
      <b-button v-else variant="primary" class="w-50" disabled>
        <b-spinner small class="mr-1"></b-spinner>
        Saving...
      </b-button>
    </div>
  </form>
</template>

<script>
import moment from '@/node_modules/moment/moment'
import InputCurrency from '@/src/components/global/InputCurrency.vue'
import {
  fleetsActions,
  maintenanceActions,
  pmsActions,
} from '@/src/Utils/helper'

export default {
  name: 'FormGOH',
  components: { InputCurrency },
  props: {
    selectedData: null,
    isFormDetail: Boolean,
  },
  data() {
    return {
      form: {
        vehicle_id: null,
        vehicle_power_id: null,
        effective_date: '',
        kick_off_goh: null,
        kick_off_goh_minutes: null,
        remarks: '',
        created_by: null,
      },
      vehicleOptions: [],
      vehiclePowerOptions: [],
      loading: false,
    }
  },
  methods: {
    ...fleetsActions,
    ...maintenanceActions,
    ...pmsActions,
    async fetchVehicles() {
      try {
        let params = {
          page: 1,
          perPage: 9999,
          showAll: true,
          active: true,
        }
        let res = await this.getFleets(params)

        if (res.status) {
          if (res.data && res.data.length > 0) {
            this.vehicleOptions = res.data.map((val) => ({
              ...val,
              text: val.name,
              value: val.id,
            }))
          }
        }
      } catch (error) {
        console.log(error)
      }
    },
    async fetchVehiclePowers() {
      try {
        if (!this.form.vehicle_id) {
          this.vehiclePowerOptions = []
          return
        }
        // Get vehicle powers from selected vehicle
        const vehicle = this.vehicleOptions.find(
          (v) => v.id === this.form.vehicle_id
        )
        if (vehicle && vehicle.powers && vehicle.powers.length > 0) {
          this.vehiclePowerOptions = vehicle.powers.map((power) => ({
            id: power.id,
            engineName: power?.powerType?.name || 'Unknown',
            powerSeries: power?.powerSeries?.name || 'Unknown',
          }))
        }
      } catch (error) {
        console.log(error)
      }
    },
    handleVehicleSelect(val) {
      this.form.vehicle_power_id = null
      this.fetchVehiclePowers()
    },
    getCreatedBy() {
      // Get created_by from logged in user
      const user = this.$store.state?.Auth?.user || {}
      this.form.created_by = user?.id || 1
    },
    setFormDataFromSelected() {
      if (!this.selectedData) return

      this.form = {
        vehicle_id: this.selectedData.vehicle_id,
        vehicle_power_id: this.selectedData.vehicle_power_id,
        effective_date: moment(this.selectedData.effective_date).format(
          'YYYY-MM-DD'
        ),
        kick_off_goh: this.selectedData.kick_off_goh,
        kick_off_goh_minutes: this.selectedData.kick_off_goh_minutes,
        remarks: this.selectedData.remarks || '',
        created_by: this.selectedData.created_by,
      }

      // Fetch powers for the selected vehicle
      this.fetchVehiclePowers()
    },
    clearForm() {
      this.form = {
        vehicle_id: null,
        vehicle_power_id: null,
        effective_date: '',
        kick_off_goh: null,
        kick_off_goh_minutes: null,
        remarks: '',
        created_by: null,
      }
      this.vehiclePowerOptions = []
      this.getCreatedBy()
    },
    async handleSubmit() {
      // Validate required fields
      if (!this.form.vehicle_id) {
        this.$bvToast.toast('Please select a fleet', {
          title: 'Validation Error',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-top-right',
          autoHideDelay: 3000,
        })
        return
      }

      if (!this.form.vehicle_power_id) {
        this.$bvToast.toast('Please select an engine', {
          title: 'Validation Error',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-top-right',
          autoHideDelay: 3000,
        })
        return
      }

      if (!this.form.effective_date) {
        this.$bvToast.toast('Please select an effective date', {
          title: 'Validation Error',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-top-right',
          autoHideDelay: 3000,
        })
        return
      }

      if (this.form.kick_off_goh === null || this.form.kick_off_goh === '') {
        this.$bvToast.toast('Please enter kick off GOH hours', {
          title: 'Validation Error',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-top-right',
          autoHideDelay: 3000,
        })
        return
      }

      this.loading = true

      try {
        // Convert hours to minutes
        const kickOffGohMinutes = this.form.kick_off_goh * 60

        const payload = {
          vehicle_id: this.form.vehicle_id,
          vehicle_power_id: this.form.vehicle_power_id,
          effective_date: this.form.effective_date,
          kick_off_goh: this.form.kick_off_goh,
          kick_off_goh_minutes: kickOffGohMinutes,
          remarks: this.form.remarks || '',
          created_by: this.form.created_by,
        }

        // Check if this is edit or create
        const isEdit = !!this.selectedData
        const res = isEdit
          ? await this.updateGOH({ id: this.selectedData.id, ...payload })
          : await this.createGOH(payload)

        if (res.status === 'success') {
          this.$swal(
            'Success',
            res?.data?.message || `Success ${isEdit ? 'Update' : 'Create'} GOH`,
            'success'
          ).then((result) => {
            if (result.isConfirmed) {
              this.$emit('close')
            }
          })
          this.$emit('refetch')
        } else {
          this.$swal(
            'Error',
            res?.data?.message || `Failed ${isEdit ? 'Update' : 'Create'} GOH`,
            'error'
          )
        }
      } catch (error) {
        console.log(error)
        this.$swal('Error', error?.message || 'An error occurred', 'error')
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    selectedData(newVal) {
      if (newVal) {
        this.setFormDataFromSelected()
      } else {
        this.clearForm()
      }
    },
  },
  mounted() {
    this.fetchVehicles()
    this.getCreatedBy()
    if (this.selectedData) {
      this.$nextTick(() => {
        this.setFormDataFromSelected()
      })
    } else {
      this.clearForm()
    }
  },
}
</script>

<style scoped></style>
